import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import './RuleForm.css';
import './ConditionalRuleForm.css';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000/api';

function ConditionalRuleForm({ fields, rules, docType, docVersion, onRuleUpdate, showToast, onFieldSelectionModeChange }) {
  const [step, setStep] = useState(0); // 0: initial, 1: select condition field, 2: select dependent fields, 3: details form
  const [formData, setFormData] = useState({
    condition_field: '',
    dependent_fields: {},
    name: '',
    type: 'bool',
    severity: 'required',
    requirement: 'atleastOne',
    customDescriptionUnformattedString: '',
    customDescriptionFormattedString: ''
  });
  const [selectedDependentFields, setSelectedDependentFields] = useState([]);
  const [selectedDependentFieldConfigs, setSelectedDependentFieldConfigs] = useState({});
  const [editingIndex, setEditingIndex] = useState(null); // Track which rule is being edited
  
  // Refs for action buttons to enable smooth scrolling
  const step1ActionsRef = useRef(null);
  const step2ActionsRef = useRef(null);
  const step3ActionsRef = useRef(null);
  
  // Scroll to action buttons when step changes
  useEffect(() => {
    const scrollToActions = () => {
      let actionsRef = null;
      if (step === 1) {
        actionsRef = step1ActionsRef.current;
      } else if (step === 2) {
        actionsRef = step2ActionsRef.current;
      } else if (step === 3) {
        actionsRef = step3ActionsRef.current;
      }
      
      if (actionsRef) {
        // Small delay to ensure DOM is updated
        setTimeout(() => {
          actionsRef.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'nearest',
            inline: 'nearest'
          });
        }, 100);
      }
    };
    
    scrollToActions();
  }, [step]);
  
  // Also scroll when dependent fields list changes (grows)
  useEffect(() => {
    if (step === 2 && step2ActionsRef.current) {
      setTimeout(() => {
        step2ActionsRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'nearest',
          inline: 'nearest'
        });
      }, 100);
    }
  }, [selectedDependentFields.length, step]);

  const generateDefaultDescriptions = (ruleName, pageNumber) => {
    if (!ruleName) {
      return { unformatted: '', formatted: '' };
    }
    const unformatted = `${ruleName} is missing on Page ${pageNumber}`;
    const formatted = `<strong> ${ruleName} </strong> is missing on <strong> Page ${pageNumber} </strong>`;
    return { unformatted, formatted };
  };

  const handleStartNewRule = () => {
    setEditingIndex(null);
    setStep(1);
    setFormData({
      condition_field: '',
      dependent_fields: {},
      name: '',
      type: 'bool',
      severity: 'required',
      requirement: 'atleastOne',
      customDescriptionUnformattedString: '',
      customDescriptionFormattedString: ''
    });
    setSelectedDependentFields([]);
    setSelectedDependentFieldConfigs({});
    // Notify parent that we're in condition field selection mode
    if (onFieldSelectionModeChange) {
      onFieldSelectionModeChange({ mode: 'condition_field', step: 1 });
    }
  };

  const handleEditRule = (index) => {
    const rule = rules[index];
    if (!rule) return;

    setEditingIndex(index);
    
    // Get page number for default descriptions
    const conditionField = fields.find(f => f.id === rule.condition_field);
    const firstDependentFieldId = Object.keys(rule.dependent_fields || {})[0];
    const firstDependentField = fields.find(f => f.id === firstDependentFieldId);
    const pageNumber = conditionField?.page || firstDependentField?.page || 1;
    
    // Generate default descriptions if they're empty
    const descriptions = generateDefaultDescriptions(rule.name || '', pageNumber);
    
    // Populate form data
    setFormData({
      condition_field: rule.condition_field || '',
      dependent_fields: rule.dependent_fields || {},
      name: rule.name || '',
      type: rule.type || 'bool',
      severity: rule.severity || 'required',
      requirement: rule.requirement || 'atleastOne',
      customDescriptionUnformattedString: rule.customDescriptionUnformattedString || descriptions.unformatted,
      customDescriptionFormattedString: rule.customDescriptionFormattedString || descriptions.formatted
    });

    // Populate dependent fields
    const dependentFieldIds = Object.keys(rule.dependent_fields || {});
    setSelectedDependentFields(dependentFieldIds);
    setSelectedDependentFieldConfigs(rule.dependent_fields || {});

    // Start at step 1 (condition field selection)
    setStep(1);
    // Notify parent that we're in condition field selection mode
    if (onFieldSelectionModeChange) {
      onFieldSelectionModeChange({ mode: 'condition_field', step: 1 });
    }
  };

  const handleConditionFieldDone = () => {
    if (!formData.condition_field) {
      showToast('Please select a condition field', 'warning');
      return;
    }
    setStep(2);
    // Notify parent that we're in dependent field selection mode
    if (onFieldSelectionModeChange) {
      onFieldSelectionModeChange({ mode: 'dependent_fields', step: 2 });
    }
  };

  const handleDependentFieldToggle = React.useCallback((fieldId) => {
    setSelectedDependentFields(prev => {
      if (prev.includes(fieldId)) {
        // Remove field
        setSelectedDependentFieldConfigs(prevConfigs => {
          const newConfigs = { ...prevConfigs };
          delete newConfigs[fieldId];
          return newConfigs;
        });
        return prev.filter(id => id !== fieldId);
      } else {
        // If requirement is "all", only allow one field
        if (formData.requirement === 'all' && prev.length >= 1) {
          showToast('When requirement is "All", you can only select one dependent field', 'warning');
          return prev;
        }
        // Add field with default config based on MongoDB type
        const field = fields.find(f => f.id === fieldId);
        const defaultType = field?.type || 'string';
        setSelectedDependentFieldConfigs(prevConfigs => ({
          ...prevConfigs,
          [fieldId]: { type: defaultType, severity: 'required' }
        }));
        return [...prev, fieldId];
      }
    });
  }, [fields, formData.requirement, showToast]);

  // Handle field selection from PDF
  const handleFieldSelectFromPDF = React.useCallback((field) => {
    if (step === 1) {
      // Step 1: Select condition field
      setFormData(prev => ({ ...prev, condition_field: field.id }));
    } else if (step === 2) {
      // Step 2: Toggle dependent field
      handleDependentFieldToggle(field.id);
    }
  }, [step, handleDependentFieldToggle]);

  // Expose handler to parent via useEffect
  React.useEffect(() => {
    if (onFieldSelectionModeChange && step === 1) {
      onFieldSelectionModeChange({ 
        mode: 'condition_field', 
        step: 1,
        selectedField: formData.condition_field,
        onFieldSelect: handleFieldSelectFromPDF
      });
    } else if (onFieldSelectionModeChange && step === 2) {
      onFieldSelectionModeChange({ 
        mode: 'dependent_fields', 
        step: 2,
        selectedFields: selectedDependentFields,
        onFieldSelect: handleFieldSelectFromPDF
      });
    } else if (onFieldSelectionModeChange && step === 0) {
      onFieldSelectionModeChange(null);
    }
  }, [step, formData.condition_field, selectedDependentFields, handleFieldSelectFromPDF, onFieldSelectionModeChange]);

  const handleDependentFieldConfigChange = (fieldId, configKey, value) => {
    setSelectedDependentFieldConfigs({
      ...selectedDependentFieldConfigs,
      [fieldId]: {
        ...selectedDependentFieldConfigs[fieldId],
        [configKey]: value
      }
    });
  };

  const handleDependentFieldsDone = () => {
    if (selectedDependentFields.length === 0) {
      showToast('Please select at least one dependent field', 'warning');
      return;
    }
    // If requirement is "all", validate that only one field is selected
    if (formData.requirement === 'all' && selectedDependentFields.length > 1) {
      showToast('When requirement is "All", you can only select one dependent field', 'warning');
      return;
    }
    // Convert selected fields to dependent_fields format
    const dependentFields = {};
    selectedDependentFields.forEach(fieldId => {
      const field = fields.find(f => f.id === fieldId);
      const defaultType = field?.type || 'string';
      dependentFields[fieldId] = selectedDependentFieldConfigs[fieldId] || { type: defaultType, severity: 'required' };
    });
    
    // Auto-set the type based on the first dependent field's type from MongoDB
    const firstDependentFieldId = selectedDependentFields[0];
    const firstDependentField = fields.find(f => f.id === firstDependentFieldId);
    const dependentFieldType = firstDependentField?.type || selectedDependentFieldConfigs[firstDependentFieldId]?.type || 'string';
    
    // Get page number from condition field, or first dependent field if condition field doesn't have page
    const conditionField = fields.find(f => f.id === formData.condition_field);
    const pageNumber = conditionField?.page || firstDependentField?.page || 1;
    
    // Generate default descriptions if they're empty
    const descriptions = generateDefaultDescriptions(formData.name || '', pageNumber);
    
    setFormData({ 
      ...formData, 
      dependent_fields: dependentFields,
      type: dependentFieldType,  // Set type from dependent field
      customDescriptionUnformattedString: formData.customDescriptionUnformattedString || descriptions.unformatted,
      customDescriptionFormattedString: formData.customDescriptionFormattedString || descriptions.formatted
    });
    setStep(3);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingIndex !== null) {
        // Update existing rule
        const params = new URLSearchParams();
        if (docVersion) params.append('version', docVersion);
        await axios.put(`${API_BASE}/rules/${docType}/conditional/${editingIndex}?${params.toString()}`, formData);
        showToast('Conditional rule updated successfully!', 'success');
      } else {
        // Create new rule
        const params = new URLSearchParams();
        if (docVersion) params.append('version', docVersion);
        await axios.post(`${API_BASE}/rules/${docType}/conditional?${params.toString()}`, formData);
        showToast('Conditional rule added successfully!', 'success');
      }
      
      onRuleUpdate();
      setStep(0);
      setEditingIndex(null);
      setFormData({
        condition_field: '',
        dependent_fields: {},
        name: '',
        type: 'bool',
        severity: 'required',
        requirement: 'atleastOne',
        customDescriptionUnformattedString: '',
        customDescriptionFormattedString: ''
      });
      setSelectedDependentFields([]);
      setSelectedDependentFieldConfigs({});
      // Notify parent that selection mode is off
      if (onFieldSelectionModeChange) {
        onFieldSelectionModeChange(null);
      }
    } catch (error) {
      console.error('Error saving rule:', error);
      showToast('Error saving rule', 'error');
    }
  };

  const handleCancel = () => {
    setStep(0);
    setEditingIndex(null);
    setFormData({
      condition_field: '',
      dependent_fields: {},
      name: '',
      type: 'bool',
      severity: 'required',
      requirement: 'atleastOne',
      customDescriptionUnformattedString: '',
      customDescriptionFormattedString: ''
    });
    setSelectedDependentFields([]);
    setSelectedDependentFieldConfigs({});
    // Notify parent that selection mode is off
    if (onFieldSelectionModeChange) {
      onFieldSelectionModeChange(null);
    }
  };

  const handleDelete = async (index) => {
    if (!window.confirm('Are you sure you want to delete this rule?')) return;
    
    try {
      const params = new URLSearchParams();
      if (docVersion) params.append('version', docVersion);
      await axios.delete(`${API_BASE}/rules/${docType}/conditional/${index}?${params.toString()}`);
      onRuleUpdate();
      showToast('Rule deleted successfully!', 'success');
    } catch (error) {
      console.error('Error deleting rule:', error);
      showToast('Error deleting rule', 'error');
    }
  };

  return (
    <div className="rule-form">
      <div className="rules-list">
        {rules.map((rule, index) => (
          <div key={index} className="rule-item">
            <div className="rule-item-header">
              <h4>{rule.name || 'Unnamed Rule'}</h4>
              <div className="rule-item-actions">
                <button
                  className="btn-secondary-small"
                  onClick={() => handleEditRule(index)}
                  style={{ marginRight: '8px' }}
                >
                  Edit
                </button>
                <button
                  className="btn-danger-small"
                  onClick={() => handleDelete(index)}
                >
                  Delete
                </button>
              </div>
            </div>
            <div className="rule-item-details">
              <p><strong>Condition Field:</strong> {rule.condition_field}</p>
              <p><strong>Dependent Fields:</strong> {Object.keys(rule.dependent_fields || {}).join(', ')}</p>
              <p><strong>Requirement:</strong> {rule.requirement}</p>
            </div>
          </div>
        ))}
      </div>

      {step === 0 && (
        <button className="btn-add" onClick={handleStartNewRule}>
          + Add New Conditional Rule
        </button>
      )}

      {step === 1 && (
        <div className="conditional-wizard-step">
          <h3>Step 1: Select Condition Field</h3>
          <p className="step-description">Click on a field in the PDF document to select it as the condition field.</p>
          {formData.condition_field && (
            <div className="selected-field-preview">
              <strong>Selected:</strong> {fields.find(f => f.id === formData.condition_field)?.name || formData.condition_field}
            </div>
          )}
          <div className="form-actions" ref={step1ActionsRef}>
            <button type="button" className="btn-primary" onClick={handleConditionFieldDone}>
              Done Selecting Condition Field
            </button>
            <button type="button" className="btn-secondary" onClick={handleCancel}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="conditional-wizard-step">
          <h3>Step 2: Select Dependent Fields</h3>
          <p className="step-description">Click on fields in the PDF document to select them as dependent fields. Click again to deselect.</p>
          
          <div className="form-group" style={{ marginBottom: '1rem' }}>
            <label>Requirement</label>
            <select
              value={formData.requirement}
              onChange={(e) => {
                const newRequirement = e.target.value;
                // If changing to "all" and more than one field is selected, keep only the first one
                if (newRequirement === 'all' && selectedDependentFields.length > 1) {
                  const firstFieldId = selectedDependentFields[0];
                  setSelectedDependentFields([firstFieldId]);
                  setSelectedDependentFieldConfigs({
                    [firstFieldId]: selectedDependentFieldConfigs[firstFieldId] || { type: 'string', severity: 'required' }
                  });
                  showToast('When requirement is "All", only one field can be selected. Other fields have been removed.', 'info');
                }
                setFormData({ ...formData, requirement: newRequirement });
              }}
            >
              <option value="atleastOne">At Least One</option>
              <option value="exactlyOne">Exactly One</option>
              <option value="all">All</option>
            </select>
            {formData.requirement === 'all' && (
              <small style={{ color: '#666', fontSize: '0.85rem', display: 'block', marginTop: '0.25rem' }}>
                When requirement is "All", you can only select one dependent field.
              </small>
            )}
          </div>
          
          {selectedDependentFields.length > 0 && (
            <div className="selected-dependent-fields-list">
              <h4>Selected Dependent Fields:</h4>
              {selectedDependentFields.map((fieldId) => {
                const field = fields.find(f => f.id === fieldId);
                // Use MongoDB type as default if config doesn't have type set
                const defaultType = field?.type || 'string';
                const config = selectedDependentFieldConfigs[fieldId] || { type: defaultType, severity: 'required' };
                return (
                  <div key={fieldId} className="selected-dependent-field-item">
                    <div className="field-info">
                      <strong title={fieldId}>{field?.name || fieldId}</strong>
                    </div>
                    <div className="field-config-inputs">
                      <select
                        value={config.type || defaultType}
                        onChange={(e) => handleDependentFieldConfigChange(fieldId, 'type', e.target.value)}
                      >
                        <option value="string">String</option>
                        <option value="bool">Boolean</option>
                        <option value="date">Date</option>
                        <option value="list">List</option>
                      </select>
                      <select
                        value={config.severity || 'required'}
                        onChange={(e) => handleDependentFieldConfigChange(fieldId, 'severity', e.target.value)}
                      >
                        <option value="required">Required</option>
                        <option value="optional">Optional</option>
                      </select>
                      <button
                        type="button"
                        className="btn-danger-small"
                        onClick={() => handleDependentFieldToggle(fieldId)}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {selectedDependentFields.length === 0 && (
            <div className="no-selection-message">
              No fields selected yet. Click on fields in the PDF document to add them.
            </div>
          )}

          <div className="form-actions" ref={step2ActionsRef}>
            <button type="button" className="btn-primary" onClick={handleDependentFieldsDone} disabled={selectedDependentFields.length === 0}>
              Done Selecting Dependent Fields
            </button>
            <button type="button" className="btn-secondary" onClick={() => {
              setStep(1);
            }}>
              Back
            </button>
            <button type="button" className="btn-secondary" onClick={handleCancel}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {step === 3 && (
        <form onSubmit={handleSubmit} className="conditional-form">
          <h3>{editingIndex !== null ? 'Edit Rule Details' : 'Step 3: Add Rule Details'}</h3>
          
          <div className="form-group">
            <label>Rule Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => {
                const newName = e.target.value;
                // Get page number from condition field or first dependent field
                const conditionField = fields.find(f => f.id === formData.condition_field);
                const firstDependentFieldId = selectedDependentFields[0];
                const firstDependentField = fields.find(f => f.id === firstDependentFieldId);
                const pageNumber = conditionField?.page || firstDependentField?.page || 1;
                
                // Auto-generate descriptions if they're empty or match the old pattern
                const descriptions = generateDefaultDescriptions(newName, pageNumber);
                const currentUnformatted = formData.customDescriptionUnformattedString || '';
                const currentFormatted = formData.customDescriptionFormattedString || '';
                
                // Only update if descriptions are empty or match the old pattern
                const oldDescriptions = generateDefaultDescriptions(formData.name || '', pageNumber);
                const shouldUpdateUnformatted = !currentUnformatted || currentUnformatted === oldDescriptions.unformatted;
                const shouldUpdateFormatted = !currentFormatted || currentFormatted === oldDescriptions.formatted;
                
                setFormData({ 
                  ...formData, 
                  name: newName,
                  customDescriptionUnformattedString: shouldUpdateUnformatted ? descriptions.unformatted : currentUnformatted,
                  customDescriptionFormattedString: shouldUpdateFormatted ? descriptions.formatted : currentFormatted
                });
              }}
              required
            />
          </div>

          <div className="form-group">
            <label>Type</label>
            <input
              type="text"
              value={formData.type}
              readOnly
              style={{ backgroundColor: '#f0f0f0', cursor: 'not-allowed' }}
            />
            <small style={{ color: '#666', fontSize: '0.85rem' }}>
              Type is automatically set based on the dependent fields
            </small>
          </div>

          <div className="form-group">
            <label>Selected Dependent Fields</label>
            <div className="dependent-fields-list">
              {selectedDependentFields.map((fieldId) => {
                const field = fields.find(f => f.id === fieldId);
                const config = selectedDependentFieldConfigs[fieldId] || {};
                return (
                  <div key={fieldId} className="dependent-field-item">
                    <span>{field?.name || fieldId} ({config.type}, {config.severity})</span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="form-group">
            <label>Custom Description (Unformatted)</label>
            <input
              type="text"
              value={formData.customDescriptionUnformattedString}
              onChange={(e) => setFormData({ ...formData, customDescriptionUnformattedString: e.target.value })}
            />
          </div>

          <div className="form-group">
            <label>Custom Description (Formatted)</label>
            <input
              type="text"
              value={formData.customDescriptionFormattedString}
              onChange={(e) => setFormData({ ...formData, customDescriptionFormattedString: e.target.value })}
            />
          </div>

          <div className="form-actions" ref={step3ActionsRef}>
            <button type="submit" className="btn-primary">
              {editingIndex !== null ? 'Update Conditional Rule' : 'Add Conditional Rule'}
            </button>
            <button type="button" className="btn-secondary" onClick={() => setStep(2)}>
              Back
            </button>
            <button type="button" className="btn-secondary" onClick={handleCancel}>
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

export default ConditionalRuleForm;
